import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';

export interface IKeyActivity {
  id: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  name?: string;
  is_published?: boolean;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IKeyActivityPaginatedResponse {
  content: IKeyActivity[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/** `silent`: tanpa toast/dialog, tanpa refetch, dan error dilempar ke pemanggil (untuk operasi batch). */
interface MutationOptions {
  silent?: boolean;
}

interface KeyActivityState {
  keyActivities: IKeyActivity[];
  paginatedData: IKeyActivityPaginatedResponse | null;
  groupedData: IKeyActivityPaginatedResponse | null;
  selectedKeyActivity: IKeyActivity | null;
  isLoading: boolean;
  error: unknown;

  createKeyActivity: (
    tenantId: string | number,
    payload: Partial<IKeyActivity>,
    options?: MutationOptions,
  ) => Promise<void>;
  getKeyActivities: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      order?: 'asc' | 'desc';
      order_by?: string;
      org_chart_node_id?: number;
      name?: string;
      published?: boolean;
    },
  ) => Promise<IKeyActivityPaginatedResponse | void>;
  getGroupedKeyActivities: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      org_chart_node_id?: number;
      name?: string;
      published?: boolean;
      order?: 'asc' | 'desc';
      order_by?: string;
    },
  ) => Promise<IKeyActivityPaginatedResponse | void>;
  getKeyActivityDetail: (tenantId: string | number, id: number | string) => Promise<IKeyActivity | void>;
  getKeyActivitiesByNode: (tenantId: string | number, orgChartNodeId: number) => Promise<IKeyActivity[]>;
  updateKeyActivity: (
    tenantId: string | number,
    id: number | string,
    payload: Partial<IKeyActivity>,
    options?: MutationOptions,
  ) => Promise<void>;
  deleteKeyActivity: (tenantId: string | number, id: number | string, options?: MutationOptions) => Promise<void>;
  bulkPublishKeyActivities: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  clearKeyActivities: () => void;
  setKeyActivities: (activities: IKeyActivity[]) => void;
}

const useKeyActivityState = create<KeyActivityState>((set, get) => ({
  keyActivities: [],
  paginatedData: null,
  groupedData: null,
  selectedKeyActivity: null,
  isLoading: false,
  error: null,
  setKeyActivities: activities => set({ keyActivities: activities }),
  clearKeyActivities: () => set({ keyActivities: [], paginatedData: null }),

  createKeyActivity: async (tenantId, payload, options) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
      await axiosInstance.post(url, payload);

      if (!options?.silent) {
        await get().getKeyActivities(tenantId);
        toast.success('Aktivitas Utama berhasil dibuat');
      }
      set({ isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
      if (options?.silent) throw error;
      errorHandler(error);
    }
  },

  getKeyActivities: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: IKeyActivityPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        keyActivities: data.data?.content || [],
        paginatedData: paginatedResponse,
        isLoading: false,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getGroupedKeyActivities: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/grouped`;
      const { data } = await axiosInstance.get(url, { params });

      // Grouped rows are one-per-jabatan with no single key-activity id; use the
      // org_chart_node_id as a stable row key for the table.
      const content: IKeyActivity[] = (data.data?.content || []).map((row: IKeyActivity) => ({
        ...row,
        id: row.org_chart_node_id ?? row.id,
      }));

      const groupedResponse: IKeyActivityPaginatedResponse = {
        content,
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({ groupedData: groupedResponse, isLoading: false });
      return groupedResponse;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getKeyActivityDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedKeyActivity: data.data, isLoading: false });
      return data.data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  // Fetch the individual key activities of one jabatan (for the detail modal),
  // without touching the page's shared list/grouped state.
  getKeyActivitiesByNode: async (tenantId, orgChartNodeId) => {
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
      const { data } = await axiosInstance.get(url, {
        params: { org_chart_node_id: orgChartNodeId, page_size: 999 },
      });
      return (data.data?.content || []) as IKeyActivity[];
    } catch (error) {
      set({ error });
      errorHandler(error);
      return [];
    }
  },

  updateKeyActivity: async (tenantId, id, payload, options) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);
      set(state => ({
        keyActivities: state.keyActivities.map(ka => (ka.id === id ? { ...ka, ...data.data } : ka)),
        selectedKeyActivity: data.data,
        isLoading: false,
      }));
      if (!options?.silent) toast.success('Aktivitas Utama berhasil diperbarui');
    } catch (error) {
      set({ error, isLoading: false });
      if (options?.silent) throw error;
      errorHandler(error);
    }
  },

  deleteKeyActivity: async (tenantId, id, options) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        keyActivities: state.keyActivities.filter(ka => ka.id !== id),
        isLoading: false,
      }));
      if (!options?.silent) toast.success('Aktivitas Utama berhasil dihapus');
    } catch (error) {
      set({ error, isLoading: false });
      if (options?.silent) throw error;
      errorHandler(error);
    }
  },

  bulkPublishKeyActivities: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        keyActivities: state.keyActivities.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(
        isPublished
          ? 'Semua Aktivitas Utama berhasil dipublikasikan'
          : 'Semua Aktivitas Utama berhasil dibatalkan publikasinya',
      );
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useKeyActivityState;
